import { AxiosResponse } from "axios";
import { t } from "i18next";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { toast } from "sonner";
import CatchBlockFunction from "../../../hooks/CatchBlockFunction";
import { registerValidation } from "../../../hooks/Validations";
import { get, patch, putType } from "../../../utils/request";
import { actions as actionsAuth } from "../../Auth/redux/slice";
import { IRegister } from "../../Auth/types";
import { CartItemType } from "../../Products/Cart/types";
import { IEmail, IHoldUpdatedItem, IOrder, IPassword } from "../types";
import {
  selectEmailForm,
  selectHoldUpdatedItems,
  selectOrderBy,
  selectOrderDetails,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectPasswordForm,
  selectRegisterForm,
  selectSearch,
} from "./selector";
import { actions } from "./slice";

export function* doUpdateProfileRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);

  const form: IRegister = yield select(selectRegisterForm);
  const isValid = registerValidation(form);
  if (!isValid) {
    return;
  }
  const data = {
    firstName: form.firstName,
    lastName: form.lastName,
    japaneseFirstName: form.japaneseFirstName,
    japaneseLastName: form.japaneseLastName,
    mobileNumber: form.mobileNumber,
    streetAddress: form.streetAddress,
    postCode: form.postCode,
  };

  try {
    yield put(actions.setButtonLoading(true));

    const response: AxiosResponse = yield putType(
      "auth/user/profile/update",
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action.payload.callback);
  } catch (error) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetProfileRequest() {
  yield delay(500);
  try {
    yield put(actions.setLoading(true));
    const response: AxiosResponse = yield call(get, "auth/user/profile");
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }

    yield put(actions.setRegisterFromData(response.data.data));
  } catch (error) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doUpdatePasswordRequest(action: {
  payload: { callback: (accessToken: string) => void };
}) {
  yield delay(500);

  const form: IPassword = yield select(selectPasswordForm);

  const data = {
    newPassword: btoa(form.newPassword),
    oldPassword: btoa(form.oldPassword),
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `auth/change-password`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }

    toast.success(response.data.message);
    yield put(actionsAuth.setToken(response.data.data.accessToken));
    yield call(action?.payload?.callback, response.data.data.accessToken);
  } catch (error) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doUpdateEmailRequest(action: {
  payload: { callback: (accessToken: string) => void };
}) {
  yield delay(500);

  const form: IEmail = yield select(selectEmailForm);

  const data = {
    newEmail: form.email,
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `auth/change-email`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback, response.data.data.accessToken);
  } catch (error) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetOrderListRequest() {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const response: AxiosResponse = yield call(
      get,
      `user/order/list?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.totalPages : 0
      )
    );
    yield put(actions.setOrderList(response.data.data.results));
    // yield call(action.payload.callback);
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doGetOrderByIdRequest(action: { payload: string }) {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const response: AxiosResponse = yield call(
      get,
      `user/order/${
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setOrderDetails(response.data.data[0]));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doOrderCancelRequest(action: {
  payload: { id: string; callback: () => void };
}) {
  yield delay(500);

  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `user/order/cancel/${
        action.payload.id !== undefined && action.payload.id !== null
          ? action.payload.id
          : ""
      }`,
      {}
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback);
  } catch (error) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doOrderUpdateRequest(action: {
  payload: { id: string; callback: () => void };
}) {
  yield delay(500);
  const holdUpdatedItems: IHoldUpdatedItem[] = yield select(
    selectHoldUpdatedItems
  );
  const orderDetails: IOrder = yield select(selectOrderDetails);

  const itemCount = orderDetails.order_items
    .filter((item) => item.itemType === CartItemType.PACKAGE)
    .map(
      (item) =>
        item.order_package_items.filter(
          (packageItem) => packageItem.availability === 2
        ).length
    );
  if (
    holdUpdatedItems.length === 0 ||
    holdUpdatedItems.length !== itemCount[0]
  ) {
    toast.error(t("myOrder.select_all_items_to_update"));
    return;
  }
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `user/order/update/package-content/${
        action.payload.id !== undefined && action.payload.id !== null
          ? action.payload.id
          : ""
      }`,
      { items: holdUpdatedItems }
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback);
  } catch (error) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetNotificationListRequest() {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const response: AxiosResponse = yield call(
      get,
      `user/notification/list?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.totalPages : 0
      )
    );
    yield put(
      actions.setTotalCount(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setNotificationList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}
export function* profileRepoSaga() {
  yield takeLatest(actions.doUpdateProfile, doUpdateProfileRequest);
  yield takeLatest(actions.doGetUserProfile, doGetProfileRequest);
  yield takeLatest(actions.doUpdatePassword, doUpdatePasswordRequest);
  yield takeLatest(actions.doUpdateEmail, doUpdateEmailRequest);
  yield takeLatest(actions.doGetOrderList, doGetOrderListRequest);
  yield takeLatest(actions.doGetOrderById, doGetOrderByIdRequest);
  yield takeLatest(actions.doOrderCancel, doOrderCancelRequest);
  yield takeLatest(actions.doOrderUpdate, doOrderUpdateRequest);
  yield takeLatest(actions.doGetNotificationList, doGetNotificationListRequest);
}
